import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { Spade, Heart, Diamond, Club, Loader2, Bug, Copy, Check, Trophy } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { VERSION, getVersionString } from "../version";

// Debug Panel Component
function DebugPanel({ envInfo, logs, error }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showText, setShowText] = useState(false);
  const [serverInfo, setServerInfo] = useState(null);

  // Fetch server info when panel is expanded
  useEffect(() => {
    if (expanded && !serverInfo) {
      const fetchServerInfo = async () => {
        try {
          const response = await axios.get('/api/debug');
          setServerInfo(response.data);
        } catch (err) {
          console.error("Failed to fetch server info:", err);
          setServerInfo({ error: err.message, status: 'unreachable' });
        }
      };
      fetchServerInfo();
    }
  }, [expanded, serverInfo]);

  const getDebugText = () => {
    return [
      `=== Client Version: ${getVersionString()} ===`,
      "",
      "=== Server Info ===",
      JSON.stringify(serverInfo || {}, null, 2),
      "",
      "=== Environment ===",
      JSON.stringify(envInfo || {}, null, 2),
      "",
      "=== Logs ===",
      ...(logs || []),
      "",
      error ? `=== Error ===\n${error}` : ""
    ].join("\n");
  };

  const copyToClipboard = () => {
    const text = getDebugText();
    
    // Create a visible textarea for manual selection
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.top = '50%';
    textarea.style.left = '50%';
    textarea.style.transform = 'translate(-50%, -50%)';
    textarea.style.width = '80%';
    textarea.style.height = '50%';
    textarea.style.zIndex = '99999';
    textarea.style.background = 'black';
    textarea.style.color = 'lime';
    textarea.style.padding = '10px';
    textarea.style.border = '2px solid lime';
    textarea.style.fontSize = '12px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    
    try {
      const success = document.execCommand('copy');
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        setTimeout(() => document.body.removeChild(textarea), 500);
      }
    } catch (e) {
      // Leave textarea visible for manual copy
      alert('自動コピー失敗。テキストを手動で選択してCtrl+Cでコピーしてください。クリックで閉じます。');
      textarea.onclick = () => document.body.removeChild(textarea);
    }
  };

  return (
    <div className="fixed bottom-4 left-4 z-[9999] max-w-sm">
      <div className="bg-black text-green-400 rounded-lg border-2 border-green-500 shadow-2xl">
        <div 
          className="flex items-center justify-between p-2 cursor-pointer border-b border-green-500"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-2">
            <Bug className="w-4 h-4" />
            <span className="text-xs font-mono font-bold">DEBUG v2</span>
          </div>
          <button 
            onClick={(e) => { e.stopPropagation(); copyToClipboard(); }}
            className="flex items-center gap-1 text-xs bg-green-500/20 px-2 py-1 rounded hover:bg-green-500/30"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        
        {expanded && (
          <div className="p-2 max-h-60 overflow-y-auto font-mono text-xs space-y-2">
            {/* Client Version */}
            <div className="bg-blue-500/10 p-2 rounded">
              <div className="text-blue-300">Client: <span className="text-blue-400 font-bold">{getVersionString()}</span></div>
            </div>

            {/* Server Info */}
            <div className="bg-purple-500/10 p-2 rounded">
              <div className="text-purple-300 mb-1">Server:</div>
              {serverInfo ? (
                <div className="space-y-0.5">
                  <div>version: <span className="text-purple-400 font-bold">{serverInfo.server_version || 'unknown'}</span></div>
                  <div>db: <span className="text-purple-400">{serverInfo.env?.db_name || 'unknown'}</span></div>
                  <div>status: <span className={serverInfo.status === 'running' ? "text-green-400" : "text-red-400"}>{serverInfo.status || 'unknown'}</span></div>
                  <div>redirect: <span className="text-purple-400 text-[10px]">{serverInfo.env?.discord_redirect_uri || 'not_set'}</span></div>
                </div>
              ) : (
                <div className="text-gray-500">Loading server info...</div>
              )}
            </div>

            {/* Environment Info */}
            <div className="bg-green-500/10 p-2 rounded">
              <div className="text-green-300 mb-1">Client Environment:</div>
              {envInfo ? (
                <div className="space-y-0.5">
                  <div>iframe: <span className={envInfo.isIframe ? "text-yellow-400" : "text-gray-400"}>{String(envInfo.isIframe)}</span></div>
                  <div>frame_id: <span className={envInfo.hasFrameId ? "text-yellow-400" : "text-gray-400"}>{String(envInfo.hasFrameId)}</span></div>
                  <div>instance_id: <span className={envInfo.hasInstanceId ? "text-yellow-400" : "text-gray-400"}>{String(envInfo.hasInstanceId)}</span></div>
                  <div>DiscordNative: <span className={envInfo.hasDiscordNative ? "text-yellow-400" : "text-gray-400"}>{String(envInfo.hasDiscordNative)}</span></div>
                </div>
              ) : (
                <div className="text-gray-500">Loading env...</div>
              )}
            </div>
            
            {/* Error */}
            {error && (
              <div className="bg-red-500/20 p-2 rounded text-red-400">
                Error: {error}
              </div>
            )}
            
            {/* Logs */}
            <div className="space-y-0.5">
              {logs && logs.length > 0 ? logs.map((log, i) => (
                <div key={i} className={log.includes('ERROR') ? 'text-red-400' : log.includes('OK') ? 'text-green-400' : 'text-gray-300'}>
                  {log}
                </div>
              )) : (
                <div className="text-gray-500">No logs yet...</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading, isDiscord, setNickname, getAvatarUrl, error: authError, apiUrl, envInfo, logs } = useAuth();
  const [nickname, setNicknameInput] = useState("");
  const [roomCode, setRoomCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  // Set initial nickname from auth context
  useEffect(() => {
    if (user?.nickname) {
      setNicknameInput(user.nickname);
    }
  }, [user]);

  // Show auth error if any
  useEffect(() => {
    if (authError) {
      toast.error(`認証エラー: ${authError}`, { duration: 10000 });
    }
  }, [authError]);

  const handleCreateRoom = async () => {
    const finalNickname = isDiscord ? user?.nickname : nickname.trim();
    
    if (!finalNickname) {
      toast.error("ニックネームを入力してください");
      return;
    }

    setLoading(true);
    try {
      // Update nickname in context for browser users
      if (!isDiscord) {
        setNickname(nickname.trim());
      }

      const response = await axios.post(`${apiUrl}/rooms`, { 
        nickname: finalNickname,
        user_id: user?.id,
        avatar_url: user?.avatar || null
      });
      const { room_code, player_id, is_host } = response.data;
      
      localStorage.setItem("playerId", player_id);
      localStorage.setItem("nickname", finalNickname);
      localStorage.setItem("isHost", is_host);
      if (user?.avatar) {
        localStorage.setItem("avatarUrl", user.avatar);
      }
      
      toast.success("部屋を作成しました");
      navigate(`/lobby/${room_code}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "部屋の作成に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleJoinRoom = async () => {
    const finalNickname = isDiscord ? user?.nickname : nickname.trim();
    
    if (!finalNickname) {
      toast.error("ニックネームを入力してください");
      return;
    }
    if (!roomCode.trim()) {
      toast.error("参加コードを入力してください");
      return;
    }

    setLoading(true);
    try {
      // Update nickname in context for browser users
      if (!isDiscord) {
        setNickname(nickname.trim());
      }

      const response = await axios.post(`${apiUrl}/rooms/${roomCode.trim().toUpperCase()}/join`, {
        nickname: finalNickname,
        user_id: user?.id,
        avatar_url: user?.avatar || null
      });
      const { room_code, player_id, is_host } = response.data;
      
      localStorage.setItem("playerId", player_id);
      localStorage.setItem("nickname", finalNickname);
      localStorage.setItem("isHost", is_host);
      if (user?.avatar) {
        localStorage.setItem("avatarUrl", user.avatar);
      }
      
      toast.success("部屋に参加しました");
      navigate(`/lobby/${room_code}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "部屋への参加に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  // Show loading screen while auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-brass" />
          <p 
            className="text-muted-foreground cursor-pointer select-none"
            onDoubleClick={() => setShowDebug(!showDebug)}
          >
            {isDiscord ? "Discordに接続中..." : "読み込み中..."}
          </p>
        </div>
        {/* Debug Panel on loading screen - hidden by default */}
        {showDebug && <DebugPanel envInfo={envInfo} logs={logs} error={authError} />}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background with spotlight effect */}
      <div className="absolute inset-0 spotlight" />
      
      {/* Decorative suits */}
      <div className="absolute top-20 left-20 opacity-5">
        <Spade className="w-32 h-32 text-foreground" />
      </div>
      <div className="absolute bottom-20 right-20 opacity-5">
        <Heart className="w-32 h-32 text-red-500" />
      </div>
      <div className="absolute top-40 right-40 opacity-5">
        <Diamond className="w-24 h-24 text-red-500" />
      </div>
      <div className="absolute bottom-40 left-40 opacity-5">
        <Club className="w-24 h-24 text-foreground" />
      </div>

      <Card className="w-full max-w-md bg-card/80 backdrop-blur-sm border-brass/30 relative z-10" data-testid="home-card">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center gap-2 text-brass">
            <Spade className="w-6 h-6" />
            <Heart className="w-6 h-6 text-red-500" />
            <Diamond className="w-6 h-6 text-red-500" />
            <Club className="w-6 h-6" />
          </div>
          <CardTitle className="font-display text-4xl tracking-wider text-foreground">
            AKINDO
          </CardTitle>
          <p className="text-muted-foreground text-sm font-light tracking-wide">
            交渉してカードを集めてポーカーの強い手役をつくろう
          </p>
          
          {/* Discord badge */}
          {isDiscord && (
            <div className="flex items-center justify-center gap-2 bg-[#5865F2]/20 text-[#5865F2] px-3 py-1 rounded-full text-xs font-medium">
              <svg className="w-4 h-4" viewBox="0 0 71 55" fill="currentColor">
                <path d="M60.1 4.9A58.5 58.5 0 0045.4.6a.2.2 0 00-.2.1 40.8 40.8 0 00-1.8 3.7 54 54 0 00-16.2 0 37.4 37.4 0 00-1.8-3.7.2.2 0 00-.2-.1 58.4 58.4 0 00-14.7 4.3.2.2 0 00-.1 0C1.5 18.5-1 31.6.5 44.5v.1a58.9 58.9 0 0018 9.1.2.2 0 00.2 0 42.2 42.2 0 003.6-5.9.2.2 0 00-.1-.3 38.8 38.8 0 01-5.5-2.7.2.2 0 010-.4l1.1-.9a.2.2 0 01.2 0 42 42 0 0035.8 0 .2.2 0 01.2 0l1.1.9a.2.2 0 010 .4 36.4 36.4 0 01-5.5 2.6.2.2 0 00-.1.3 47.3 47.3 0 003.6 5.9.2.2 0 00.2.1 58.7 58.7 0 0018-9.1v-.1c1.7-15.2-3-28.4-12.4-40a.2.2 0 000-.1zM23.7 36.5c-3.5 0-6.3-3.2-6.3-7.1s2.8-7.1 6.3-7.1 6.4 3.2 6.3 7.1c0 3.9-2.8 7.1-6.3 7.1zm23.3 0c-3.5 0-6.3-3.2-6.3-7.1s2.8-7.1 6.3-7.1 6.4 3.2 6.3 7.1c0 3.9-2.8 7.1-6.3 7.1z"/>
              </svg>
              Discord Activity
            </div>
          )}
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* User Profile (Discord or Browser) */}
          {user && (
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <img 
                src={getAvatarUrl()} 
                alt="Avatar" 
                className="w-10 h-10 rounded-full object-cover"
              />
              <div className="flex-1 min-w-0">
                {isDiscord ? (
                  <p className="font-medium truncate">{user.nickname}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">ブラウザモード</p>
                )}
              </div>
            </div>
          )}

          {/* Nickname Input (only for browser mode) */}
          {!isDiscord && (
            <div className="space-y-2">
              <label className="text-xs font-light text-muted-foreground uppercase tracking-widest">
                ニックネーム
              </label>
              <Input
                data-testid="nickname-input"
                value={nickname}
                onChange={(e) => setNicknameInput(e.target.value)}
                placeholder="あなたの名前"
                maxLength={12}
                className="bg-input border-border text-foreground placeholder:text-muted-foreground"
              />
            </div>
          )}

          <Tabs defaultValue="create" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-muted">
              <TabsTrigger value="create" className="font-medium text-sm" data-testid="tab-create">
                部屋を作成
              </TabsTrigger>
              <TabsTrigger value="join" className="font-medium text-sm" data-testid="tab-join">
                部屋に参加
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="create" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                新しいゲーム部屋を作成し、友達を招待しましょう。
              </p>
              <Button
                data-testid="create-room-btn"
                onClick={handleCreateRoom}
                disabled={loading || (!isDiscord && !nickname.trim())}
                className="w-full bg-brass text-accent-foreground hover:bg-brass/90 font-medium tracking-wide uppercase text-sm"
              >
                {loading ? "作成中..." : "部屋を作成"}
              </Button>
            </TabsContent>
            
            <TabsContent value="join" className="space-y-4 mt-4">
              <div className="space-y-2">
                <label className="text-xs font-light text-muted-foreground uppercase tracking-widest">
                  参加コード
                </label>
                <Input
                  data-testid="room-code-input"
                  value={roomCode}
                  onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                  placeholder="6桁のコード"
                  maxLength={6}
                  className="bg-input border-border text-foreground placeholder:text-muted-foreground font-data text-center text-xl tracking-[0.5em]"
                />
              </div>
              <Button
                data-testid="join-room-btn"
                onClick={handleJoinRoom}
                disabled={loading || (!isDiscord && !nickname.trim()) || !roomCode.trim()}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium tracking-wide uppercase text-sm"
              >
                {loading ? "参加中..." : "部屋に参加"}
              </Button>
            </TabsContent>
          </Tabs>

          {/* Ranking Button */}
          <div className="pt-4">
            <Button
              variant="outline"
              onClick={() => navigate("/ranking")}
              className="w-full border-brass/30 text-brass hover:bg-brass/10 flex items-center justify-center gap-2"
            >
              <Trophy className="w-4 h-4" />
              ランキングを見る
            </Button>
          </div>

          <div className="pt-4 border-t border-border">
            <h3 
              className="font-display text-sm text-brass mb-2 cursor-pointer select-none"
              onDoubleClick={() => setShowDebug(!showDebug)}
            >
              ゲームルール
            </h3>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• 4人でプレイ</li>
              <li>• 7枚のカードから最強の5枚役を作る</li>
              <li>• カードとチップを交渉・交換</li>
              <li>• ラウンドごとにチップを獲得・損失</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Debug Panel - Hidden by default, toggle with double-click on "ゲームルール" */}
      {showDebug && <DebugPanel envInfo={envInfo} logs={logs} error={authError} />}
    </div>
  );
}
